import * as React from 'react';
import { useCallback } from 'react';
import { Controller, Path, UseControllerProps } from 'react-hook-form';

import { TextFieldProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  DateTimePicker as MuiDateTimePicker,
  DateTimePickerProps,
} from '@mui/x-date-pickers';

type Props<TFieldValues> = Omit<
  Partial<DateTimePickerProps<Date>>,
  // These three carry the react-hook-form binding. Letting a caller pass them
  // would silently detach the field from the form.
  'value' | 'onChange' | 'renderInput'
> &
  Omit<UseControllerProps<TFieldValues>, 'name'> & {
    label?: string;
    fieldName: Path<TFieldValues>;
    // Unlike DatePicker, helperText is allowed here: it is shown as guidance
    // whenever the field has no validation error.
    textFieldProps?: Omit<TextFieldProps, 'error' | 'label'>;
  };

export type DateTimePickerPropsWithoutConnectionProps<TFieldValues> = Omit<
  Props<TFieldValues>,
  'control'
>;

export default function DateTimePicker<TFieldValues>({
  fieldName,
  control,
  label,
  defaultValue,
  rules,
  shouldUnregister,
  textFieldProps,
  ...props
}: Props<TFieldValues>) {
  const renderField = useCallback(
    ({ field, fieldState: { error } }) => (
      <MuiDateTimePicker<Date>
        {...props}
        label={label}
        value={field.value ?? null}
        onChange={newValue => {
          field.onChange(newValue);
        }}
        renderInput={params => (
          <TextField
            margin="normal"
            fullWidth
            {...(textFieldProps || {})}
            {...params}
            label={label}
            // params.error carries MUI's own bound violations (disableFuture,
            // minDateTime). Overwriting it with only the react-hook-form error
            // would leave typed out-of-range values looking valid until submit.
            error={!!error || !!params.error}
            helperText={error?.message || textFieldProps?.helperText}
            onBlur={event => {
              params.onBlur?.(event);
              field.onBlur();
            }}
            inputRef={field.ref}
          />
        )}
      />
    ),
    [label, props, textFieldProps]
  );

  return (
    <Controller
      render={renderField}
      control={control}
      name={fieldName}
      defaultValue={defaultValue}
      rules={rules}
      shouldUnregister={shouldUnregister}
    />
  );
}
